import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUp } from 'lucide-react';
import { blockRegistry } from './blocks';
import WebsiteHeader from './WebsiteHeader';
import WebsiteFooter from './WebsiteFooter';
import { getThemeCSS, getResolvedTheme } from './themeDefaults';
import { getBlockFontOverrides } from './blocks/common/commonDefaults';
import { getCoachByPlayerProfile } from '../../services/firestoreService';
import { getLibraryItems } from '../../services/libraryService';
import { normalizeAccessLevel, ACCESS_LEVELS } from '../../constants/accessLevels';

/**
 * PreviewMode - Full preview renderer for blocks
 * @param {Array} blocks - Array of block objects to render
 * @param {Array} youtubeVideos - YouTube videos data
 * @param {string} profileId - Profile ID for data fetching
 * @param {string} profileName - Display name for header
 * @param {string} profileLogo - Logo URL for header
 * @param {string} username - Username for building coaching URLs
 * @param {string} accentColor - Theme accent color for components (legacy)
 * @param {object} theme - Full theme object with colors and fonts
 * @param {boolean} footerEnabled - Whether footer is visible
 * @param {string} footerEmail - Email to display in footer
 * @param {string} footerTagline - Tagline to display in footer
 */
const PreviewMode = ({
  blocks,
  youtubeVideos = [],
  photos = [],
  profileId,
  profileName,
  profileLogo,
  username,
  headerNavLinks = [],
  headerNavStyle = 'buttons',
  headerStyle = {},
  accentColor = '#749555',
  theme = {},
  footerEnabled = true,
  footerEmail = '',
  footerTagline = '',
  socialLinks = {},
  hideFooter = false,
  hideHeader = false,
  announcement,
  isOnOwnDomain = false,
}) => {
  // Check if coaching block exists
  const hasCoachingBlock = blocks.some((b) => b.type === 'coaching');

  // Fetch coach data once for sharing between WebsiteHeader and CoachingRenderer
  const [coachData, setCoachData] = useState(null);
  const [hasLibraryContent, setHasLibraryContent] = useState(false);

  useEffect(() => {
    if (hasCoachingBlock && profileId) {
      getCoachByPlayerProfile(profileId)
        .then((coach) => {
          setCoachData(coach);
          if (coach?.id) {
            getLibraryItems(coach.id, null)
              .then((items) => {
                const hasPublic = items.some(item => normalizeAccessLevel(item.accessLevel) !== ACCESS_LEVELS.ADMIN);
                setHasLibraryContent(hasPublic);
              })
              .catch(() => setHasLibraryContent(false));
          }
        })
        .catch(console.error);
    }
  }, [hasCoachingBlock, profileId]);

  // Get resolved theme with all defaults applied
  const resolvedTheme = getResolvedTheme(theme);

  // Get CSS variables for the theme
  const themeCSS = getThemeCSS(theme);

  // Build a single deduplicated Google Fonts URL from theme + block overrides
  const allFonts = new Set([resolvedTheme.fonts.heading, resolvedTheme.fonts.body]);
  for (const font of getBlockFontOverrides(blocks)) {
    allFonts.add(font);
  }
  allFonts.delete('inherit');
  allFonts.delete('system-ui');

  const fontsUrl = allFonts.size > 0
    ? `https://fonts.googleapis.com/css2?${Array.from(allFonts).map(f => `family=${f.replace(/ /g, '+')}:wght@400;500;600;700`).join('&')}&display=swap`
    : null;

  if (blocks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-gray-400">
          No blocks to preview. Add some blocks in Edit mode.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Load Google Fonts if needed */}
      {fontsUrl && (
        <link
          href={fontsUrl}
          rel="stylesheet"
        />
      )}

      <div
        className="website-preview"
        style={{
          backgroundColor: resolvedTheme.colors.pageBackground,
          width: '100%',
          minWidth: '100%',
          ...themeCSS,
        }}
      >
        {/* Announcement Bar — skip when parent layout (ProfileSiteLayout) already renders it */}
        {!hideHeader && announcement?.enabled && announcement?.text && (
          <AnnouncementBar
            text={announcement.text}
            bgColor={announcement.bgColor}
            textColor={announcement.textColor}
            accentColor={resolvedTheme.colors.accent}
            buttonText={announcement.buttonText}
            buttonLink={announcement.buttonLink}
          />
        )}

        {/* Website Header — skip when parent layout provides it */}
        {!hideHeader && (
          <WebsiteHeader
            profileName={profileName}
            profileLogo={profileLogo}
            username={username}
            hasCoachingBlock={hasCoachingBlock}
            hasLibraryContent={hasLibraryContent}
            headerNavLinks={headerNavLinks}
            headerNavStyle={headerNavStyle}
            headerStyle={headerStyle}
            theme={resolvedTheme}
            coach={coachData}
          />
        )}

        {/* Content blocks */}
        <div
          style={{
            fontFamily: `var(--theme-font-body)`,
          }}
        >
          {blocks.map((block) => {
            const BlockRenderer = blockRegistry[block.type]?.Renderer;

            if (!BlockRenderer) {
              return (
                <div
                  key={block.id}
                  id={`block-${block.id}`}
                  className="p-8 text-center"
                  style={{
                    backgroundColor: resolvedTheme.colors.sectionBackground,
                    color: resolvedTheme.colors.mutedColor,
                  }}
                >
                  Unknown block type: {block.type}
                </div>
              );
            }

            return (
              <div key={block.id} id={`block-${block.id}`}>
                <BlockRenderer
                  block={block}
                  youtubeVideos={youtubeVideos}
                  photos={photos}
                  profileId={profileId}
                  username={username}
                  isEmbedded={block.type === 'coaching'}
                  accentColor={resolvedTheme.colors.accent}
                  theme={resolvedTheme}
                  isOnOwnDomain={isOnOwnDomain}
                  {...(block.type === 'coaching' && coachData ? {
                    coach: coachData,
                    coachId: coachData.id,
                    initialUsername: username,
                    initialPlayerProfile: { photoUrl: profileLogo },
                  } : {})}
                />
              </div>
            );
          })}
        </div>

        {/* Website Footer - rendered if enabled */}
        {!hideFooter && (
          <WebsiteFooter
            footerEmail={footerEmail}
            footerTagline={footerTagline}
            profileName={profileName}
            profileLogo={profileLogo}
            username={username}
            theme={theme}
            socialLinks={socialLinks}
            headerNavLinks={headerNavLinks}
            hasCoachingBlock={hasCoachingBlock}
            hasLibraryContent={hasLibraryContent}
          />
        )}

        {/* Scroll-to-Top Button */}
        <ScrollToTopButton accent={resolvedTheme.colors.accent} />
      </div>
    </>
  );
};

/** Thin announcement strip rendered above the website header */
const AnnouncementBar = ({ text, bgColor, textColor, accentColor, buttonText, buttonLink }) => {
  const bg = bgColor || accentColor || '#749555';

  // Use explicit textColor if set, otherwise auto-detect from bg luminance
  const resolvedTextColor = textColor || (() => {
    if (!bg || !bg.startsWith('#') || bg.length < 7) return '#ffffff';
    const num = parseInt(bg.slice(1), 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#111827' : '#ffffff';
  })();

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: bg,
        color: resolvedTextColor,
        padding: '6px 16px',
        textAlign: 'center',
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {text}
      {buttonText && buttonLink && (
        <>
          {' '}
          <a
            href={buttonLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: resolvedTextColor,
              textDecoration: 'underline',
              fontWeight: 700,
            }}
          >
            {buttonText} &rarr;
          </a>
        </>
      )}
    </div>
  );
};

/** Floating scroll-to-top button — appears after scrolling 400px */
const ScrollToTopButton = ({ accent = '#749555' }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Auto-detect text color from accent luminance
  const textColor = (() => {
    if (!accent || !accent.startsWith('#') || accent.length < 7) return '#ffffff';
    const num = parseInt(accent.slice(1), 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#111827' : '#ffffff';
  })();

  return (
    <button
      onClick={handleClick}
      aria-label="Scroll to top"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: accent,
        color: textColor,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 35,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <ChevronUp size={22} />
    </button>
  );
};

export default PreviewMode;
